import { useDispatch, useSelector } from "react-redux";
import { cx } from "../../Util/classHelpers";
import TableV2 from "../TableV2/tableV2";
import styles from "./accounts.module.css";
import StarIcon from "../Icons/star-icon";
import { useEffect, useMemo, useState } from "react";
import ThreeDotsIcon from "../Icons/three-dots";
import Popup from "../CommonComponents/popup/Popup";
import React from "react";
import getMagicLink from "../../Actions/magicLinkAction";
import { MAGIC_LINK } from "../../Reducers/magicLinkReducer";
import { SET_ACTIVE_COMPANY } from "../../Reducers/activeCompanyReducer";
import { ownPermissionsSelector } from "../../Reducers/ownRoleReducer";
import AccountsControls from "./AccountsControls";


const Actions = ({ account, isLast }) => {
  const [open, setOpen] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const dispatch = useDispatch();
  const magicLinkObj = useSelector(state => state.magicLink);

  useEffect(() => {
    if (magicLinkObj !== null && showLoading) {
      const link = magicLinkObj.getMagicLink();
      setShowLoading(false);
      setOpen(false);
      dispatch({ type: MAGIC_LINK, payload: null });
      window.open(link, "_blank");
    }
  }, [magicLinkObj, dispatch, showLoading]);

  const handleAccessDispatch = (e) => {
    e.stopPropagation();
    setShowLoading(true);
    dispatch(getMagicLink(account.adminId, account.companyId));
  };

  return (
    <div className={styles.actionsWrapper}>
      <div className={styles.actionsIcon} onClick={() => setOpen(true)}>
        <ThreeDotsIcon />
      </div>
      <Popup open={open} onClose={() => setOpen(false)} className={cx(styles.actionsPopupWrapper, isLast && styles.actionsPopupWrapperLast)}>
        <div className={styles.actionsPopup}>
          <div
            className={styles.actionsPopupItem}
            onClick={handleAccessDispatch}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            Access Dispatch Dashboard
            {showLoading && (
              <span className="spinner-border spinner-border-sm disabledLoading" style={{ marginRight: 6 }}></span>
            )}
          </div>
        </div>
      </Popup>
    </div>
  );
};

const columnConfig = [
  {
    id: "businessName",
    label: "Business Name",
    data: ({ account, handleSelectCompany }) => (
      <div className={cx(styles.tableEntry, styles.companyNameEntry)}>
        <span onClick={handleSelectCompany}>{account?.companyName || "-"}</span>
      </div>
    ),
    headerClass: styles.tableHeaderEntry,
  },
  {
    id: "adminName",
    label: "Admin Name",
    data: ({ account }) => (
      <div className={styles.tableEntry}>{account?.adminName || "-"}</div>
    ),
    headerClass: styles.tableHeaderEntry,
  },
  {
    id: "adminEmail",
    label: "Admin Email",
    data: ({ account }) => (
      <div className={styles.tableEntry}>{account?.email || "-"}</div>
    ),
    headerClass: styles.tableHeaderEntry,
  },
  {
    id: "orderRating",
    label: "Order Rating",
    data: ({ orderRating }) => (
      <div className={cx(styles.tableEntry, styles.ratingEntry)}>
        {orderRating === "N/A" ? (
          "N/A"
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div>
              {orderRating?.toFixed ? orderRating.toFixed(2) : orderRating}
            </div>
            <StarIcon width={18} height={18} />
          </div>
        )}
      </div>
    ),
    headerClass: styles.tableHeaderEntry,
  },
  {
    id: "driverRating",
    label: "Driver Rating",
    data: ({ driverRating }) => (
      <div className={cx(styles.tableEntry, styles.ratingEntry)}>
        {driverRating === "N/A" ? (
          "N/A"
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div>
              {driverRating?.toFixed ? driverRating.toFixed(2) : driverRating}
            </div>
            <StarIcon width={18} height={18} />
          </div>
        )}
      </div>
    ),
    headerClass: styles.tableHeaderEntry,
  },
  {
    id: "currentPlan",
    label: "Current Plan",
    data: ({ planName }) => (
      <div className={styles.tableEntry}>{planName || "-"}</div>
    ),
    headerClass: styles.tableHeaderEntry,
  },
  {
    id: "country",
    label: "Country",
    data: ({ account }) => (
      <div className={styles.tableEntry}>{account?.country || "-"}</div>
    ),
    headerClass: styles.tableHeaderEntry,
  },
  {
    id: "actions",
    label: "",
    data: ({ account, isLast }) => (
      <div className={styles.actionsTableEntry}>
        <Actions account={account} isLast={isLast} />
      </div>
    ),
    headerClass: styles.tableHeaderEntry,
    tableClass: styles.actionsTd,
  },
];

const tableConfig = {
    columns: columnConfig,
    pagination: false,
    scrollable: false,
    hoverable: false,
};



const AccountsTable = ({ processedData, pageSize, totalPages, currentPageIndex, setCurrentPageIndex, getFilter, setFilter }) => {
  const dispatch = useDispatch();
  const ownPermissions = useSelector(ownPermissionsSelector);

  const tableData = processedData.map((item, index) => ({
      account: item.account,
      planName: item.planName,
      orderRating: item.orderRating,
      driverRating: item.driverRating,
      handleSelectCompany: () => {
        dispatch({ type: SET_ACTIVE_COMPANY, payload: item.account.companyId });
      },
      isLast: index === pageSize - 1 || index === processedData.length - 1,
  }));

  const processedTableConfig = useMemo(() => {
    if (ownPermissions.canAccessCompany()) {
      return tableConfig;
    } else {
      return {
        ...tableConfig,
        columns: columnConfig.filter(col => col.id !== "actions"),
      }
    }
  }, [ownPermissions])

  return (
    <div className={styles.tableSection}>
        <div className={styles.tableHeaderWithControls}>
            <div className={styles.tableHeader}>
                All Accounts
            </div>
            {getFilter && setFilter && (
                <AccountsControls getFilter={getFilter} setFilter={setFilter} />
            )}
        </div>
        <div className={cx(styles.tableContainer, "table--no-td-padding", "table--no-last-separator", processedTableConfig.columns.find(col => col.id === "actions") && "table--last-col-sticky")}>
            <div className={styles.tableScrollContainer}>
              <TableV2 data={tableData} config={processedTableConfig} emptyText="No accounts found" />
            </div>
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <div
                  className={cx(
                    styles.paginationBtn,
                    currentPageIndex === 0 && styles.paginationBtnDisabled
                  )}
                  onClick={() => currentPageIndex > 0 && setCurrentPageIndex(currentPageIndex - 1)}
                >
                  Previously
                </div>
                <div className={styles.paginationInfo}>
                  Page {currentPageIndex + 1} of {totalPages}
                </div>
                <div
                  className={cx(
                    styles.paginationBtn,
                    currentPageIndex === totalPages - 1 && styles.paginationBtnDisabled
                  )}
                  onClick={() => currentPageIndex < totalPages - 1 && setCurrentPageIndex(currentPageIndex + 1)}
                >
                    Next
                </div>
              </div>
            )}
        </div>
    </div>
  )
}
export default AccountsTable
